import {
  type SanityFilter,
  type SanitySort,
} from '@data/sanity/queries/types/cart'
import { clampRange, parseOptionalParameter } from '@lib/helpers'
import {
  getFilterFromUrlParameter,
  getSortParameterValue,
  useProductFilter,
  useProductSort,
} from '@lib/product-collection'
import { ProductCollectionContext } from '@lib/product-collection-context'
import { ProductCollectionQuickFilterContext } from '@lib/product-collection-quick-filter-context'
import { LayoutContext } from '@lib/site-context'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import CollectionFilterAndSort from './filter-and-sort'
import CollectionFooter from './footer'
import CollectionProducts from './products'
import CollectionQuickFilter from './quick-filter'

interface CollectionProps {
  title?: string
  featuredProductIds: number[]
  paginationLimit: number
  sort?: SanitySort
  filter?: SanityFilter
  className?: string
}

const Collection = ({
  title,
  featuredProductIds,
  paginationLimit,
  sort,
  filter,
  className,
}: CollectionProps) => {
  const {
    collectionProducts,
    activeCollectionUrlParameters,
    defaultSortOption,
    filterGroups,
    updateCollectionUrlParameters,
  } = useContext(ProductCollectionContext)
  const { quickFilterOptions, quickFilterSelectedValue } = useContext(
    ProductCollectionQuickFilterContext,
  )
  const { headerHeight } = useContext(LayoutContext)

  const [hasPagination, setHasPagination] = useState(
    // TODO: Initial value doesn't match value set in effect hook which could cause a layout shift.
    paginationLimit > 0 && collectionProducts.length > paginationLimit,
  )
  const [currentCount, setCurrentCount] = useState(
    hasPagination ? paginationLimit : collectionProducts.length,
  )

  const productCardRef = useRef<(HTMLDivElement | null)[]>([])

  // Active filter and sort values
  const activeSortValue = getSortParameterValue(activeCollectionUrlParameters)
  const activeFilters = useMemo(() => {
    return activeCollectionUrlParameters
      .filter((activeParameter) => {
        return !['page', 'sort'].includes(activeParameter.name)
      })
      .map(getFilterFromUrlParameter(filterGroups))
  }, [activeCollectionUrlParameters, filterGroups])
  const activeFilterProductIds = useMemo(() => {
    return activeFilters.reduce<number[] | undefined>(
      (previousValue, currentValue) => {
        return previousValue ?? currentValue.productIds
      },
      undefined,
    )
  }, [activeFilters])
  const activeQuickFilterProductIds = useMemo(() => {
    const selectedQuickFilterOption = quickFilterOptions.find(
      (quickFilterOption) => {
        return quickFilterOption.value === quickFilterSelectedValue
      },
    )

    return selectedQuickFilterOption?.featuredProductIds
  }, [quickFilterOptions, quickFilterSelectedValue])

  // Manage filtering, sorting and pagination
  const filteredProducts = useProductFilter(collectionProducts, activeFilters)
  const sortedProducts = useProductSort(
    filteredProducts,
    // Use default sort option as fallback, since when sort option value matches the default it's empty
    activeSortValue ?? defaultSortOption,
    // Use:
    // - quick filter's featured product ordering,
    // - filter's linked collection product ordering,
    // - collection or shop page product ordering.
    activeQuickFilterProductIds ?? activeFilterProductIds ?? featuredProductIds,
  )
  const paginatedProducts = useMemo(
    () => [...sortedProducts.slice(0, currentCount)],
    [currentCount, sortedProducts],
  )

  // Manage product pagination
  useEffect(() => {
    const pageParameter = activeCollectionUrlParameters.find(
      (activeParameter) => activeParameter.name === 'page',
    )
    const page = Number(parseOptionalParameter(pageParameter?.value) ?? 1)
    const productCount = sortedProducts.length
    const pageCount = Math.ceil(productCount / paginationLimit)

    if (page <= 1 || page > pageCount) {
      return
    }

    const newCount = clampRange(paginationLimit * page, 1, productCount)

    if (newCount !== currentCount) {
      setCurrentCount(newCount)
    }
  }, [
    activeCollectionUrlParameters,
    currentCount,
    paginationLimit,
    sortedProducts,
  ])

  // Focus on the first link in the first or last product card
  useEffect(() => {
    const pageProductIndex =
      currentCount < sortedProducts.length
        ? currentCount - paginationLimit
        : sortedProducts.length - 1
    productCardRef.current[pageProductIndex]
      ?.querySelector<HTMLAnchorElement>('[href]')
      ?.focus({ preventScroll: true })
  }, [currentCount, paginationLimit, sortedProducts.length])

  // Update pagination status
  useEffect(
    () => setHasPagination(currentCount < sortedProducts.length),
    [currentCount, sortedProducts],
  )

  // Handle loading more products
  const loadMore = useCallback(() => {
    const newCount = clampRange(
      currentCount + paginationLimit,
      1,
      sortedProducts.length,
    )

    const newPage = Math.ceil(newCount / paginationLimit)

    setCurrentCount(newCount)
    updateCollectionUrlParameters([
      {
        name: 'page',
        value: newPage > 1 ? `${newPage}` : null,
      },
    ])
  }, [
    currentCount,
    paginationLimit,
    sortedProducts,
    updateCollectionUrlParameters,
  ])

  const clearFilters = () => {
    updateCollectionUrlParameters(
      activeFilters.map((activeFilter) => ({
        name: activeFilter.name,
        value: null,
      })),
    )
  }

  if (collectionProducts.length === 0) {
    return null
  }

  return (
    <section className={className}>
      {!!title && (
        <div className="container flex justify-between pt-10 pb-2.5 border-b border-gray">
          <h1>{title}</h1>
        </div>
      )}

      <div
        className="sticky z-10"
        style={{
          top: headerHeight,
        }}
      >
        <CollectionQuickFilter />
        <CollectionFilterAndSort
          filter={filter}
          sort={sort}
          processedProductCount={sortedProducts.length}
          paginatedProcessedProductCount={paginatedProducts.length}
          activeFilters={activeFilters}
          filterGroups={filterGroups}
          activeParameters={activeCollectionUrlParameters}
          updateParameters={updateCollectionUrlParameters}
        />
      </div>

      <CollectionProducts
        productCardRef={productCardRef}
        productCount={sortedProducts.length}
        products={paginatedProducts}
        filters={activeFilters}
        parameters={activeCollectionUrlParameters}
      />

      <CollectionFooter
        title={title}
        isFilterActive={!!filter?.isActive}
        processedProductCount={sortedProducts.length}
        hasPagination={hasPagination}
        clearFilters={clearFilters}
        loadMore={loadMore}
      />
    </section>
  )
}

export default Collection
